import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, PlayCircle, PencilLine, LibraryBig, Pause, Trophy } from "lucide-react";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { getHome } from "@/lib/api";
import { toast } from "sonner";

const LEVEL_KEY = { basic: "levelBasic", intermediate: "levelInter", advanced: "levelAdvanced" };
const NEXT_CTA_KEY = { intermediate: "progressTryIntermediate", advanced: "progressTryAdvanced" };

const truncate = (s, n) => {
  const clean = (s || "").trim();
  if (clean.length <= n) return clean;
  return `${clean.slice(0, n - 1).trimEnd()}…`;
};

export default function Home() {
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    getHome()
      .then(setData)
      .catch(() => toast.error(tr("errorGeneric")));
    // eslint-disable-next-line
  }, []);

  const contextLabel =
    (lang === "en" ? data?.industry_label_en : data?.industry_label_es) ||
    data?.industry_effective ||
    data?.profile?.industry ||
    data?.work_context;
  const industryEff = data?.industry_effective;
  const isOther = industryEff === "other";
  const personalized = data?.personalized_create;
  const hasPersonalized = !!personalized;
  const needsJobDesc = data?.needs_job_description;
  const showGeneralSection = hasPersonalized || isOther; // separately-labeled general practice for any prefilled-fallback industry

  const openPersonalizedCreate = () => {
    if (!personalized) return;
    navigate("/create", { state: { prefill: personalized } });
  };

  return (
    <AppShell>
      <section className="px-5 pt-4 pb-8 space-y-5" data-testid="home-screen">
        <div>
          <span className="wpe-eyebrow">{tr("homeGreeting")}</span>
          <h1 className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="home-title">
            {user?.email?.split("@")[0]}
          </h1>
          {contextLabel && (
            <div className="text-[12px] text-[rgb(var(--wpe-slate))]" data-testid="home-active-context">
              <span className="wpe-eyebrow">{tr("workProfile")}: </span>
              <span>{contextLabel}</span>
              {isOther && personalized?.description && (
                <span className="text-[rgb(var(--wpe-muted))]"> · {truncate(personalized.description, 80)}</span>
              )}
            </div>
          )}
          <p className="text-[12px] text-[rgb(var(--wpe-muted))]">{tr("homePlanNote")}</p>
          <p className="text-[11px] text-[rgb(var(--wpe-slate))] mt-1" data-testid="home-choice-explain">
            {tr("practiceChoiceExplain")}
          </p>
        </div>

        {/* Rehearse-a-real-work-situation hero — reuses existing /create with saved-profile prefill.
            Positioned above Continue and recommendations so it is impossible to miss.
            Does NOT generate anything on Home load. */}
        <button
          type="button"
          onClick={() => navigate("/create", personalized ? { state: { prefill: personalized } } : undefined)}
          className="wpe-motiv-banner w-full text-left block"
          data-testid="home-work-rehearsal-cta"
        >
          <div className="flex items-start gap-3 relative z-10">
            <span className="w-12 h-12 rounded-2xl bg-white/25 backdrop-blur text-white flex items-center justify-center shrink-0">
              <Sparkles size={22} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/90">
                {tr("planNext")}
              </div>
              <div className="text-[18px] font-extrabold text-white leading-tight mt-1" data-testid="home-work-rehearsal-title">
                {tr("homeWorkRehearsalTitle")}
              </div>
              <div className="text-[13px] text-white/90 mt-1 leading-snug" data-testid="home-work-rehearsal-body">
                {tr("homeWorkRehearsalBody")}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-white bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">
                {tr("homeWorkRehearsalCta")} <ArrowRight size={14} />
              </div>
            </div>
          </div>
        </button>

        {data?.continue_session && (
          <button
            type="button"
            onClick={() => navigate(`/session/${data.continue_session.session_id}`)}
            className="wpe-card wpe-card-mint w-full flex items-center gap-3 text-left"
            data-testid="continue-session-btn"
          >
            <PlayCircle size={22} className="text-[rgb(var(--wpe-teal))] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="wpe-eyebrow">
                {data.continue_session.status === "paused" ? tr("resumePractice") : tr("homeContinue")}
              </div>
              <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                {lang === "en" ? data.continue_session.title_en : data.continue_session.title_es}
              </div>
              {typeof data.continue_session.turns_used === "number" && (
                <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
                  {tr("pausedTurns")
                    .replace("{n}", data.continue_session.turns_used)
                    .replace("{max}", data.continue_session.max_turns)}
                </div>
              )}
            </div>
            <ArrowRight size={16} className="text-[rgb(var(--wpe-muted))]" />
          </button>
        )}

        {/* Progression V1: 0–5 core-practice progress + milestone.
            Only shown for a learner with a named industry and a saved
            level. Milestone is a practice-completion count, NOT a
            proficiency score. */}
        {data?.core_progress && !data.core_progress.milestone_reached && (
          <div className="wpe-card space-y-2" data-testid="home-core-progress">
            <div className="wpe-eyebrow">{tr("progressCoreEyebrow")}</div>
            <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))]" data-testid="home-core-progress-count">
              {tr("progressCoreCount")
                .replace("{done}", data.core_progress.completed_count)
                .replace("{total}", data.core_progress.target)}
            </div>
            <div className="flex gap-1" aria-hidden="true" data-testid="home-core-progress-dots">
              {Array.from({ length: data.core_progress.target }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 flex-1 rounded-full ${
                    i < data.core_progress.completed_count
                      ? "bg-[rgb(var(--wpe-teal))]"
                      : "bg-[rgb(var(--wpe-border))]"
                  }`}
                />
              ))}
            </div>
            <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
              {tr("progressCoreHint").replace(
                "{level}",
                tr(LEVEL_KEY[data.core_progress.level] || "levelBasic")
              )}
            </div>
          </div>
        )}

        {data?.core_progress?.milestone_reached && (
          <div className="wpe-card wpe-card-mint space-y-3" data-testid="home-core-milestone">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-white/60 text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
                <Trophy size={20} />
              </span>
              <div className="flex-1 min-w-0">
                <div
                  className="text-[15px] font-bold text-[rgb(var(--wpe-navy))] leading-snug"
                  data-testid="home-core-milestone-title"
                >
                  {tr("progressMilestoneTitle")
                    .replace("{total}", data.core_progress.target)
                    .replace(
                      "{level}",
                      tr(LEVEL_KEY[data.core_progress.level] || "levelBasic")
                    )}
                </div>
                <div
                  className="text-[11px] text-[rgb(var(--wpe-slate))] mt-1"
                  data-testid="home-core-milestone-note"
                >
                  {tr("progressMilestoneNote")}
                </div>
                <div className="text-[12px] font-semibold text-[rgb(var(--wpe-navy))] mt-2">
                  {tr("progressMilestoneAsk")}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {data.core_progress.next_level && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/practice?level=${data.core_progress.next_level}`)
                  }
                  className="wpe-cta w-full text-[13px]"
                  data-testid="home-core-milestone-next-level-btn"
                >
                  {tr(NEXT_CTA_KEY[data.core_progress.next_level] || "progressTryIntermediate")}
                  <ArrowRight size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  navigate(`/practice?level=${data.core_progress.level}&mode=remix`)
                }
                className="wpe-secondary w-full text-[13px]"
                data-testid="home-core-milestone-again-btn"
              >
                {tr("progressPracticeVariation")}
              </button>
              <button
                type="button"
                onClick={() => navigate("/create")}
                className="wpe-secondary w-full text-[13px]"
                data-testid="home-core-milestone-create-btn"
              >
                {tr("progressCreateOwn")}
              </button>
            </div>
          </div>
        )}

        {/* Additional paused practices (beyond the most-recent Continue card) */}
        {Array.isArray(data?.paused_sessions) && data.paused_sessions.length > 1 && (
          <div className="space-y-2" data-testid="home-paused-list">
            <h2 className="wpe-eyebrow flex items-center gap-1">
              <Pause size={12} /> {tr("pausedList")}
            </h2>
            <p className="text-[11px] text-[rgb(var(--wpe-muted))]">{tr("pausedListSub")}</p>
            {data.paused_sessions
              .filter((p) => p.session_id !== data?.continue_session?.session_id)
              .map((p) => (
                <button
                  key={p.session_id}
                  type="button"
                  onClick={() => navigate(`/session/${p.session_id}`)}
                  className="wpe-card w-full text-left flex items-start gap-3"
                  data-testid={`home-paused-${p.session_id}`}
                >
                  <span className="w-9 h-9 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
                    <PlayCircle size={16} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                      {lang === "en" ? p.title_en : p.title_es}
                    </div>
                    <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
                      {tr("pausedTurns").replace("{n}", p.turns_used).replace("{max}", p.max_turns)}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-[rgb(var(--wpe-muted))]" />
                </button>
              ))}
          </div>
        )}

        {/* Personalized "Create practice for: <desc>" — for Other-with-description AND for named industries with zero curated matches (truthful prefilled fallback) */}
        {hasPersonalized && (
          <div data-testid="home-personalized-create">
            <span className="wpe-eyebrow">{tr("homePersonalizedForYouLabel")}</span>
            <button
              type="button"
              onClick={openPersonalizedCreate}
              className="wpe-card w-full text-left flex items-start gap-3 mt-1"
              data-testid="home-personalized-create-btn"
            >
              <span className="w-10 h-10 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
                <Sparkles size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug" data-testid="home-personalized-title">
                  {tr("homePersonalizedCreateTitle").replace("{desc}", truncate(personalized.description, 60))}
                </div>
                <div className="text-[11px] text-[rgb(var(--wpe-muted))] mt-0.5">
                  {tr("homePersonalizedCreateSub")}
                </div>
              </div>
              <ArrowRight size={16} className="text-[rgb(var(--wpe-muted))] shrink-0" />
            </button>
          </div>
        )}

        {/* Prompt: Other user without a description */}
        {isOther && needsJobDesc && (
          <div className="wpe-card space-y-2" data-testid="home-other-needs-desc">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
                <PencilLine size={16} />
              </span>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))]">{tr("homeOtherNeedsDescTitle")}</div>
                <div className="text-[11px] text-[rgb(var(--wpe-muted))] mt-0.5">{tr("homeOtherNeedsDescSub")}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/settings")}
              className="wpe-cta w-full text-[13px]"
              data-testid="home-other-add-in-settings-btn"
            >
              {tr("homeOtherAddInSettings")}
            </button>
          </div>
        )}

        {/* Standard "Create my own situation" — hidden when a personalized prefilled card already covers this learner */}
        {!hasPersonalized && (
          <button
            type="button"
            onClick={() => navigate("/create")}
            className="wpe-card w-full text-left flex items-start gap-3"
            data-testid="create-situation-cta"
          >
            <span className="w-9 h-9 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center">
              <Sparkles size={18} />
            </span>
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))]">{tr("homeCreate")}</div>
              <div className="text-[11px] text-[rgb(var(--wpe-muted))]">{tr("homeCreateSub")}</div>
            </div>
          </button>
        )}

        {/* Review card — only shown when saved evidence supports it */}
        {data?.review_card && (
          <button
            type="button"
            onClick={() => navigate(`/briefing/${data.review_card.scenario_id}`)}
            className="wpe-card wpe-card-gold w-full text-left flex items-start gap-3"
            data-testid="home-plan-review"
          >
            <span className="w-9 h-9 rounded-full wpe-chip-gold flex items-center justify-center shrink-0">
              <PencilLine size={16} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="wpe-eyebrow">{tr("planReviewLabel")}</div>
              <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                {lang === "en" ? data.review_card.title_en : data.review_card.title_es}
              </div>
              <div className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid="home-plan-review-reason">
                {lang === "en" ? data.review_card.reason_en : data.review_card.reason_es}
              </div>
            </div>
            <ArrowRight size={16} className="text-[rgb(var(--wpe-muted))]" />
          </button>
        )}

        {/* Challenge card — only shown when ≥2 distinct saved evaluated completions */}
        {data?.challenge_card && (
          <button
            type="button"
            onClick={() => navigate(`/briefing/${data.challenge_card.scenario_id}`)}
            className="wpe-card wpe-card-coral w-full text-left flex items-start gap-3"
            data-testid="home-plan-challenge"
          >
            <span className="w-9 h-9 rounded-full wpe-chip-coral flex items-center justify-center shrink-0">
              <Sparkles size={16} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="wpe-eyebrow">{tr("planChallengeLabel")}</div>
              <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                {lang === "en" ? data.challenge_card.title_en : data.challenge_card.title_es}
              </div>
              <div className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid="home-plan-challenge-reason">
                {lang === "en" ? data.challenge_card.reason_en : data.challenge_card.reason_es}
              </div>
            </div>
            <ArrowRight size={16} className="text-[rgb(var(--wpe-muted))]" />
          </button>
        )}

        {/* Honest empty-state hint for zero-history learners */}
        {data && !data.has_saved_history && !data.review_card && !data.challenge_card && (
          <p className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid="home-plan-no-history">
            {tr("planNoHistoryHint")}
          </p>
        )}

        <div className="space-y-2" data-testid="home-recs">
          <h2 className="wpe-eyebrow">{tr("homeRecommended")}</h2>

          {/* Empty-state fallback (only when there's neither curated nor personalized content) */}
          {!hasPersonalized && data?.fallback_no_match && (
            <div className="wpe-hint text-[12px]" data-testid="home-no-match">
              {tr("homeCreateSub")}
              <button type="button" onClick={() => navigate("/create")} className="wpe-cta w-full text-[12px] mt-2" data-testid="home-fallback-create-btn">
                {tr("homeCreate")}
              </button>
            </div>
          )}

          {(data?.recommended || []).map((s) => (
            <Link
              to={`/briefing/${s.id}`}
              key={s.id}
              className="wpe-scenario-card"
              data-testid={`home-rec-${s.id}`}
            >
              <span className="w-10 h-10 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
                <Sparkles size={16} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                  {lang === "en" ? s.title_en : s.title_es}
                </div>
                <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
                  {lang === "en" ? s.category_en : s.category_es} · {tr(LEVEL_KEY[s.difficulty] || "levelBasic")} · {s.time_min}m
                </div>
              </div>
              <ArrowRight size={16} className="text-[rgb(var(--wpe-muted))]" />
            </Link>
          ))}

          {/* Clearly labeled general-practice section for any learner whose personalized flow is prefilled-fallback (never surfaced as job-specific) */}
          {showGeneralSection && (
            <div className="pt-2" data-testid="home-general-practice-section">
              <div className="text-[12px] font-semibold text-[rgb(var(--wpe-navy))]">{tr("homeGeneralPracticeTitle")}</div>
              <div className="text-[11px] text-[rgb(var(--wpe-muted))] mt-0.5 mb-2">{tr("homeGeneralPracticeSub")}</div>
              <button
                type="button"
                onClick={() => navigate("/practice?browse=all")}
                className="wpe-secondary w-full text-[13px]"
                data-testid="home-general-practice-btn"
              >
                <LibraryBig size={14} /> {tr("homeGeneralPracticeCta")}
              </button>
            </div>
          )}
        </div>

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}
